import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  IconLock,
  IconLoader2,
  IconAlertCircle,
  IconKey,
  IconCircleCheck,
  IconEye,
  IconEyeOff
} from '@tabler/icons-react';
import LogoIcon from '../components/LogoIcon';
import { authAPI } from '../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Неверная или отсутствующая ссылка сброса пароля');
      return;
    }
    if (password.length < 8) {
      setError('Пароль должен быть минимум 8 символов');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Пароль должен содержать минимум одну заглавную букву');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      setError('Пароль должен содержать минимум один специальный символ (!@#$%^&*...)');
      return;
    }
    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Не удалось изменить пароль');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <LogoIcon />
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              XN Connect
            </span>
          </Link>
        </div>

        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8">
          {done ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IconCircleCheck className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Пароль изменён
                </h2>
                <p className="text-gray-600 dark:text-dark-400 text-sm">
                  Теперь вы можете войти в аккаунт с новым паролем.
                </p>
              </div>
              <Link
                to="/login"
                className="inline-block w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white transition-colors text-center"
              >
                Войти
              </Link>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IconKey className="w-8 h-8 text-primary-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Новый пароль
                </h2>
                <p className="text-gray-600 dark:text-dark-400 text-sm">
                  Введите новый пароль дважды
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-6">
                  <IconAlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                    Новый пароль
                  </label>
                  <div className="relative">
                    <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Введите новый пароль"
                      className="w-full pl-10 pr-10 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      {showPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                    Подтверждение пароля
                  </label>
                  <div className="relative">
                    <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Повторите пароль"
                      className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <IconLoader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <span>Изменить пароль</span>
                  )}
                </button>
              </form>
            </>
          )}

          <Link
            to="/login"
            className="block text-center mt-6 text-sm text-gray-600 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Вернуться ко входу
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
